import { useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { useApiClient } from '@/shared/api'

import {
  BasicSettingsForm,
  type BasicSettingsFormValues,
  CustomerLimitsForm,
  type CustomerLimitsFormValues,
  AsteriskConfigForm,
  type AsteriskConfigFormValues,
  ControllerConfigForm,
  type ControllerConfigFormValues,
  InstanceInfoCard,
  InstanceActionsCard,
  type SerializedInstance,
  parsePjsipEndpoints,
  DEFAULT_PJSIP_CONF,
} from './'
import { useUserData } from "@/entities/user";

interface AdminData {
  campaign: {
    id: number
    name: string
    timezone: string
    dialerIsActive: boolean
    controller: {
      callEndpoint: string
      phoneNumberPrefix: string
      playHello: boolean
      noStage1AMD: boolean
      noStage2AMD: boolean
      callTimeout: number
      botEndpoint: string
      botExtension: string
      kickBotWhenTranferCompleted: boolean
      managerEndpoint: string
      managerPhoneNumberPrefix: string
      transferMode: string
    }
    customerLimits: {
      maxDialPerSecond: number
      maxActiveConferences: number
      maxSimultaneousCalls: number
      ratio: number
    }
  }
  instances: {
    asterisk: SerializedInstance | null
    controller: SerializedInstance | null
  }
  hasDeployedInstances: boolean
  availableEndpoints: string[]
}

function useDialerAdmin(campaignId: number) {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: ['dialer-admin', campaignId],
    queryFn: async () => {
      return apiClient<AdminData>(`/dialer/campaign/admin?campaignId=${campaignId}`)
    },
    enabled: !!campaignId,
    refetchInterval: 5000, // Poll every 5 seconds for task status updates
  })
}

// Combined form values for first setup mode
interface FirstSetupFormValues
  extends BasicSettingsFormValues,
    CustomerLimitsFormValues,
    AsteriskConfigFormValues,
    ControllerConfigFormValues {}

function FirstSetupMode({
  campaign,
  instances,
  availableEndpoints,
  onRefresh,
}: {
  campaign: AdminData['campaign']
  instances: AdminData['instances']
  availableEndpoints: string[]
  onRefresh: () => void
}) {
  const apiClient = useApiClient()
  const [isSaving, setIsSaving] = useState(false)
  const [isDeploying, setIsDeploying] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FirstSetupFormValues>({
    defaultValues: {
      // Basic settings
      timezone: campaign.timezone,
      dialerIsActive: campaign.dialerIsActive,
      // Customer limits
      maxDialPerSecond: campaign.customerLimits.maxDialPerSecond,
      maxActiveConferences: campaign.customerLimits.maxActiveConferences,
      maxSimultaneousCalls: campaign.customerLimits.maxSimultaneousCalls,
      ratio: campaign.customerLimits.ratio,
      // Asterisk config
      asteriskInstanceSize: instances.asterisk?.instanceSize || 't3.small',
      pjsipConf: (instances.asterisk?.params?.['pjsip-conf'] as string) || DEFAULT_PJSIP_CONF,
      requiresElasticIp: instances.asterisk?.requiresElasticIp ?? true,
      // Controller config
      controllerInstanceSize: instances.controller?.instanceSize || 't3.small',
      callEndpoint: campaign.controller.callEndpoint,
      phoneNumberPrefix: campaign.controller.phoneNumberPrefix,
      callTimeout: campaign.controller.callTimeout,
      playHello: campaign.controller.playHello,
      noStage1AMD: campaign.controller.noStage1AMD,
      noStage2AMD: campaign.controller.noStage2AMD,
      botEndpoint: campaign.controller.botEndpoint,
      botExtension: campaign.controller.botExtension || '',
      kickBotWhenTranferCompleted: campaign.controller.kickBotWhenTranferCompleted,
      managerEndpoint: campaign.controller.managerEndpoint,
      managerPhoneNumberPrefix: campaign.controller.managerPhoneNumberPrefix,
      transferMode: campaign.controller.transferMode || 'conference',
    },
  })

  const saveConfiguration = async (values: FirstSetupFormValues, deploy: boolean) => {
    const setLoading = deploy ? setIsDeploying : setIsSaving
    setLoading(true)

    try {
      // 1. Save basic settings and controller config to dialer_config
      await apiClient('/dialer/campaign/admin', {
        method: 'PUT',
        body: JSON.stringify({
          campaignId: campaign.id,
          timezone: values.timezone,
          dialerIsActive: values.dialerIsActive,
          controller: {
            callEndpoint: values.callEndpoint,
            phoneNumberPrefix: values.phoneNumberPrefix,
            playHello: values.playHello,
            noStage1AMD: values.noStage1AMD,
            noStage2AMD: values.noStage2AMD,
            callTimeout: values.callTimeout,
            botEndpoint: values.botEndpoint,
            botExtension: values.botExtension,
            kickBotWhenTranferCompleted: values.kickBotWhenTranferCompleted,
            managerEndpoint: values.managerEndpoint,
            managerPhoneNumberPrefix: values.managerPhoneNumberPrefix,
            transferMode: values.transferMode,
          },
          customerLimits: {
            maxDialPerSecond: values.maxDialPerSecond,
            maxActiveConferences: values.maxActiveConferences,
            maxSimultaneousCalls: values.maxSimultaneousCalls,
            ratio: values.ratio,
          },
        }),
      })

      // 2. Save instance configurations
      await apiClient('/dialer/campaign/instance', {
        method: 'POST',
        body: JSON.stringify({
          action: deploy ? 'deploy' : 'save',
          campaignId: campaign.id,
          asterisk: {
            instanceSize: values.asteriskInstanceSize,
            requiresElasticIp: values.requiresElasticIp,
            params: {
              'pjsip-conf': values.pjsipConf,
            },
          },
          controller: {
            instanceSize: values.controllerInstanceSize,
            requiresElasticIp: false,
          },
        }),
      })

      toast.success(
        deploy ? 'Configuration saved and deployment started' : 'Configuration saved successfully'
      )
      onRefresh()
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const onSave = handleSubmit((values) => saveConfiguration(values, false))
  const onSaveAndDeploy = handleSubmit((values) => saveConfiguration(values, true))

  return (
    <form className="space-y-6">
      {/* Type assertions needed because parent form includes all fields */}
      <BasicSettingsForm register={register as any} control={control as any} errors={errors} />
      <CustomerLimitsForm register={register as any} errors={errors} />
      <AsteriskConfigForm register={register as any} control={control as any} errors={errors} />
      <ControllerConfigForm
        register={register as any}
        control={control as any}
        errors={errors}
        availableEndpoints={availableEndpoints}
      />

      <div className="flex items-center justify-end gap-4">
        <Button type="button" variant="outline" onClick={onSave} disabled={isSaving || isDeploying}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
        <Button type="button" onClick={onSaveAndDeploy} disabled={isSaving || isDeploying}>
          {isDeploying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save and Deploy
        </Button>
      </div>
    </form>
  )
}

// Form values for deployed mode - each tab has its own form
interface BasicTabFormValues extends BasicSettingsFormValues, CustomerLimitsFormValues {}
interface AsteriskTabFormValues extends AsteriskConfigFormValues {}
interface ControllerTabFormValues extends ControllerConfigFormValues {}

function DeployedMode({
  campaign,
  instances,
  availableEndpoints,
  onRefresh,
}: {
  campaign: AdminData['campaign']
  instances: AdminData['instances']
  availableEndpoints: string[]
  onRefresh: () => void
}) {
  const apiClient = useApiClient()
  const [activeTab, setActiveTab] = useState('basic')
  const [isSaving, setIsSaving] = useState(false)

  // Revalidate data when switching tabs
  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value)
      onRefresh()
    },
    [onRefresh]
  )

  const handleActionComplete = useCallback(() => {
    onRefresh()
  }, [onRefresh])

  // Basic tab form
  const basicForm = useForm<BasicTabFormValues>({
    defaultValues: {
      timezone: campaign.timezone,
      dialerIsActive: campaign.dialerIsActive,
      maxDialPerSecond: campaign.customerLimits.maxDialPerSecond,
      maxActiveConferences: campaign.customerLimits.maxActiveConferences,
      maxSimultaneousCalls: campaign.customerLimits.maxSimultaneousCalls,
      ratio: campaign.customerLimits.ratio,
    },
  })

  // Asterisk tab form
  const asteriskForm = useForm<AsteriskTabFormValues>({
    defaultValues: {
      asteriskInstanceSize: instances.asterisk?.instanceSize || 't3.small',
      pjsipConf: (instances.asterisk?.params?.['pjsip-conf'] as string) || DEFAULT_PJSIP_CONF,
      requiresElasticIp: instances.asterisk?.requiresElasticIp ?? true,
    },
  })

  // Controller tab form
  const controllerForm = useForm<ControllerTabFormValues>({
    defaultValues: {
      controllerInstanceSize: instances.controller?.instanceSize || 't3.small',
      callEndpoint: campaign.controller.callEndpoint,
      phoneNumberPrefix: campaign.controller.phoneNumberPrefix,
      callTimeout: campaign.controller.callTimeout,
      playHello: campaign.controller.playHello,
      noStage1AMD: campaign.controller.noStage1AMD,
      noStage2AMD: campaign.controller.noStage2AMD,
      botEndpoint: campaign.controller.botEndpoint,
      botExtension: campaign.controller.botExtension || '',
      kickBotWhenTranferCompleted: campaign.controller.kickBotWhenTranferCompleted,
      managerEndpoint: campaign.controller.managerEndpoint,
      managerPhoneNumberPrefix: campaign.controller.managerPhoneNumberPrefix,
      transferMode: campaign.controller.transferMode || 'conference',
    },
  })

  const saveBasicSettings = async (values: BasicTabFormValues) => {
    setIsSaving(true)
    try {
      await apiClient('/dialer/campaign/admin', {
        method: 'PUT',
        body: JSON.stringify({
          campaignId: campaign.id,
          timezone: values.timezone,
          dialerIsActive: values.dialerIsActive,
          customerLimits: {
            maxDialPerSecond: values.maxDialPerSecond,
            maxActiveConferences: values.maxActiveConferences,
            maxSimultaneousCalls: values.maxSimultaneousCalls,
            ratio: values.ratio,
          },
        }),
      })

      toast.success('Basic settings saved')
    } catch (error: any) {
      toast.error(error.message || 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const asteriskIsUp = instances.asterisk?.status === 'up'

  const saveAsteriskConfig = async (values: AsteriskTabFormValues) => {
    if (!instances.asterisk) return

    setIsSaving(true)
    try {
      // Save configuration to database
      await apiClient('/dialer/campaign/instance', {
        method: 'POST',
        body: JSON.stringify({
          action: 'save',
          campaignId: campaign.id,
          asterisk: {
            instanceSize: values.asteriskInstanceSize,
            requiresElasticIp: values.requiresElasticIp,
            params: {
              'pjsip-conf': values.pjsipConf,
            },
          },
        }),
      })

      // Server automatically schedules update_pjsip task if pjsip conf changed and instance is running
      toast.success('Asterisk configuration saved')

      onRefresh()
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setIsSaving(false)
    }
  }

  const saveControllerConfig = async (values: ControllerTabFormValues) => {
    setIsSaving(true)
    try {
      // Save controller settings to dialer_config
      await apiClient('/dialer/campaign/admin', {
        method: 'PUT',
        body: JSON.stringify({
          campaignId: campaign.id,
          controller: {
            callEndpoint: values.callEndpoint,
            phoneNumberPrefix: values.phoneNumberPrefix,
            playHello: values.playHello,
            noStage1AMD: values.noStage1AMD,
            noStage2AMD: values.noStage2AMD,
            callTimeout: values.callTimeout,
            botEndpoint: values.botEndpoint,
            botExtension: values.botExtension,
            kickBotWhenTranferCompleted: values.kickBotWhenTranferCompleted,
            managerEndpoint: values.managerEndpoint,
            managerPhoneNumberPrefix: values.managerPhoneNumberPrefix,
            transferMode: values.transferMode,
          },
        }),
      })

      // Save controller instance size
      if (instances.controller) {
        await apiClient('/dialer/campaign/instance', {
          method: 'POST',
          body: JSON.stringify({
            action: 'save',
            campaignId: campaign.id,
            controller: {
              instanceSize: values.controllerInstanceSize,
              requiresElasticIp: false,
            },
          }),
        })
      }

      toast.success('Controller configuration saved')
      onRefresh()
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setIsSaving(false)
    }
  }

  const controllerIsUp = instances.controller?.status === 'up'

  // Check if each instance has a pending task (used to disable server size)
  const asteriskHasPendingTask =
    instances.asterisk?.lastTask?.status === 'new' ||
    instances.asterisk?.lastTask?.status === 'in_progress'
  const controllerHasPendingTask =
    instances.controller?.lastTask?.status === 'new' ||
    instances.controller?.lastTask?.status === 'in_progress'

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="basic">Basic</TabsTrigger>
        <TabsTrigger value="asterisk">Asterisk</TabsTrigger>
        <TabsTrigger value="controller">Controller</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="space-y-6 mt-6">
        <form onSubmit={basicForm.handleSubmit(saveBasicSettings)} className="space-y-6">
          <BasicSettingsForm
            register={basicForm.register as any}
            control={basicForm.control as any}
            errors={basicForm.formState.errors}
          />
          <CustomerLimitsForm
            register={basicForm.register as any}
            errors={basicForm.formState.errors}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </form>
      </TabsContent>

      <TabsContent value="asterisk" className="space-y-6 mt-6">
        <InstanceInfoCard instance={instances.asterisk} title="Asterisk" />

        <InstanceActionsCard
          instance={instances.asterisk}
          campaignId={campaign.id}
          stack="asterisk"
          onActionComplete={handleActionComplete}
        />

        <form onSubmit={asteriskForm.handleSubmit(saveAsteriskConfig)} className="space-y-6">
          <AsteriskConfigForm
            register={asteriskForm.register}
            control={asteriskForm.control}
            errors={asteriskForm.formState.errors}
            instanceSizeDisabled={asteriskIsUp || asteriskHasPendingTask}
            hideElasticIp={instances.asterisk?.elasticIpAllocationId !== null}
          />
          {instances.asterisk && (
            <div className="flex justify-end">
              <Button type="submit" disabled={isSaving || asteriskHasPendingTask}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Configuration
              </Button>
            </div>
          )}
        </form>
      </TabsContent>

      <TabsContent value="controller" className="space-y-6 mt-6">
        <InstanceInfoCard instance={instances.controller} title="Controller" />

        <InstanceActionsCard
          instance={instances.controller}
          campaignId={campaign.id}
          stack="controller"
          onActionComplete={handleActionComplete}
        />

        <form onSubmit={controllerForm.handleSubmit(saveControllerConfig)} className="space-y-6">
          <ControllerConfigForm
            register={controllerForm.register}
            control={controllerForm.control}
            errors={controllerForm.formState.errors}
            instanceSizeDisabled={controllerIsUp || controllerHasPendingTask}
            availableEndpoints={availableEndpoints}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </form>
      </TabsContent>
    </Tabs>
  )
}

function NoAccessBlock() {
  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <div className="text-center">
        <h2 className="text-heading font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You need organization admin access to view this page.</p>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  )
}

export default function DialerAdminPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const campaignIdNum = parseInt(campaignId || '0')
  const queryClient = useQueryClient()

  const { role } = useUserData();
  const isOrgAdmin = role === 'org:admin'

  const { data, isLoading, refetch } = useDialerAdmin(campaignIdNum)

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dialer-admin', campaignIdNum] })
    refetch()
  }, [queryClient, campaignIdNum, refetch])

  // Compute available endpoints from pjsip.conf
  const availableEndpoints = useMemo(() => {
    if (!data) return []
    const pjsipConf =
      (data.instances.asterisk?.params?.['pjsip-conf'] as string) || DEFAULT_PJSIP_CONF
    return parsePjsipEndpoints(pjsipConf)
  }, [data])

  if (!isOrgAdmin) {
    return <NoAccessBlock />
  }

  if (isLoading || !data) {
    return <LoadingSkeleton />
  }

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-heading font-bold text-gray-900">Dialer Admin Settings</h1>
          <p className="text-body text-gray-600 mt-1">Campaign: {data.campaign.name}</p>
        </div>

        {data.hasDeployedInstances ? (
          <DeployedMode
            campaign={data.campaign}
            instances={data.instances}
            availableEndpoints={availableEndpoints}
            onRefresh={handleRefresh}
          />
        ) : (
          <FirstSetupMode
            campaign={data.campaign}
            instances={data.instances}
            availableEndpoints={availableEndpoints}
            onRefresh={handleRefresh}
          />
        )}
      </div>
    </div>
  )
}